import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Sun, Moon, Eye, EyeOff, Loader2 } from 'lucide-react';
import CryptoJS from 'crypto-js'
import { login } from '../redux/slices/authSlice';
import { API_BASE_URL } from '../constants';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cryptoKey = process.env.REACT_APP_API_BASE_URL;
  
  const [formData, setFormData] = useState({
    admissionNo: '',
    resultCode: '',
  });
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log("formData",formData);

    // if (!formData.admissionNo || !formData.resultCode) {
    if (!formData.admissionNo) {
      // setError('Please enter both admission number and result code.');
      setError('Please enter both admission number.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Call backend API for student login with admission number and result code
      // const response = await fetch(`${API_BASE_URL}/api/web/student/login-with-code`, {
      const response = await fetch(`${API_BASE_URL}/api/web/student/login-with-no-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          admissionNo: formData.admissionNo, 
          // resultCode: formData.resultCode, 
          role: 'student' 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed. Please check your credentials.');
      }

      const data = await response.json();
      const userData = data?.user;

      // Save token to localStorage
      if (userData?.token) {
        // const encryptedToken = CryptoJS.AES.encrypt(userData.token, cryptoKey).toString();
        // console.log({encryptedToken});
        
        localStorage.clear();
        localStorage.setItem('authToken', userData.token);
        // localStorage.setItem('authToken', encryptedToken);
        localStorage.setItem('userData', JSON.stringify(userData));
      }

      // Dispatch login to Redux
      dispatch(login({ user: userData, role: 'student' }));

      // Navigate to student dashboard
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 ${darkMode ? 'dark' : ''}`}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md relative">
        {/* Dark mode toggle */}
        <button
          onClick={() => setDarkMode(!darkMode)}
          className="absolute top-4 right-4 p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Student Portal</h1>
          <p className="text-gray-600 dark:text-gray-400">Sign in to access your profile and result</p>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Admission Number
            </label>
            <input
              type="text"
              name="admissionNo"
              value={formData.admissionNo}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
              placeholder="Enter your admission number"
              required
              disabled={loading}
            />
          </div>
          
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Result Codee
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="resultCode"
                value={formData.resultCode}
                onChange={handleInputChange}
                className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="Enter your result code"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={loading}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div> */}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Footer links */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Forgot your password?{' '}
            <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
              Reset it here
            </a>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Need help? Contact{' '}
            <a href="mailto:support@schoolportal.com" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
              support@schoolportal.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 